import { useCallback, useEffect } from "react";
import { useBlocker } from "react-router-dom";
import t from "../../shared/translations";

/**
 * A custom hook that warns the user about unsaved changes when they try to navigate away
 * from the current page, either through browser navigation or React Router.
 *
 * @param isDirty - Whether there are unsaved changes
 * @param message - The message to show in the browser's native confirmation dialog
 * @returns An object containing a function to confirm navigation
 */
export default function useUnsavedChangesWarning(
    isDirty: boolean,
    message: string = t("shared.warning.unsaved-changes"),
): { confirmNavigation: (callback: () => void) => void } {
    const blocker = useBlocker(isDirty);
    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (isDirty) {
                event.preventDefault();
                event.returnValue = message;
                return message;
            }
        };

        if (isDirty) {
            window.addEventListener("beforeunload", handleBeforeUnload);
        }

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isDirty, message]);

    useEffect(() => {
        if (blocker.state === "blocked" && isDirty) {
            const proceed = window.confirm(message);
            if (proceed) {
                blocker.proceed();
            } else {
                blocker.reset();
            }
        }
    }, [blocker, isDirty, message]);

    const confirmNavigation = useCallback(
        (callback: () => void) => {
            if (isDirty) {
                if (window.confirm(message)) {
                    callback();
                }
            } else {
                callback();
            }
        },
        [isDirty, message],
    );

    return { confirmNavigation };
}
